import {computed, ref} from 'vue';
import {defineStore} from 'pinia';
import {useAsyncData} from '#imports';
import {useErrorHandler} from '@malt/utils';
import type {FetchError, LanguageCode, ProjectConditionContentResponse} from '@api';
import {ProjectConditionsApi} from '@api';
import {apiConfiguration} from '~/configuration/apiConfiguration';

export const useProjectConditionsStore = defineStore('project-conditions', () => {
    const projectConditionsApi = new ProjectConditionsApi(apiConfiguration);
    const {handleError, handleFatalError} = useErrorHandler();

    const isLoading = ref(false);
    const projectConditionResponse = ref<ProjectConditionContentResponse | null>(null);

    async function fetchProjectCondition(projectConditionId: string, language?: LanguageCode) {
        isLoading.value = true;

        try {
            // Language parameter is required by the API, default to English if not provided
            const languageToUse: LanguageCode = language ?? 'EN';

            const {data, error} = await useAsyncData(
                'project-condition',
                () =>
                    projectConditionsApi.getProjectCondition({
                        projectConditionId,
                        language: languageToUse,
                    }),
                {
                    deep: true,
                },
            );

            if (error.value) {
                handleError(error.value.message);
                return;
            }

            if (data.value) {
                projectConditionResponse.value = data.value;
            }
        } catch (e) {
            handleFatalError('Could not load project condition data', (e as FetchError).statusCode ?? 500);
        } finally {
            isLoading.value = false;
        }
    }

    const projectCondition = computed<ProjectConditionContentResponse | null>(() => projectConditionResponse.value ?? null);
    const projectConditionContent = computed<string>(() => projectConditionResponse.value?.content ?? '');

    return {
        isLoading,
        projectCondition,
        projectConditionContent,

        fetchProjectCondition,
    };
});
